import '@/styles/globals.scss'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import App from './App.vue'
import { CreateVuetifyApp } from './plugins/vuetify'
import settings from '../configs/settings.json'
import assistantConfig from '../configs/assistant.json'

window.catalog_config = (window as any).catalog_config || (settings as any)
window.ai_chat_config = (window as any).ai_chat_config || (assistantConfig as any)

const rootElement = document.getElementById('one-800-products-app')

if (rootElement) {
  // Create Shadow Root for main app
  const shadow = rootElement.attachShadow({ mode: 'open' })

  // Create Shadow Root for body modals (avoids transforms while maintaining style isolation)
  let modalHost = document.getElementById('one-800-products-modal-host')
  let modalShadow: ShadowRoot | null = null
  let modalContainer: HTMLElement | null = null

  const is_admin_bar = document.body.classList.contains('admin-bar') || !!document.getElementById('wpadminbar')
  const base_classes = `outfit-font${is_admin_bar ? ' has-admin-bar' : ''}`

  if (!modalHost) {
    modalHost = document.createElement('div')
    modalHost.id = 'one-800-products-modal-host'
    modalHost.style.position = 'relative'
    modalHost.style.zIndex = '999999'
    document.body.appendChild(modalHost)
    modalShadow = modalHost.attachShadow({ mode: 'open' })
    modalContainer = document.createElement('div')
    modalContainer.id = 'app-modal-root'
    modalContainer.className = base_classes
    modalShadow.appendChild(modalContainer)
  } else {
    modalShadow = modalHost.shadowRoot
    modalContainer = modalShadow?.getElementById('app-modal-root') || null
    if (modalContainer && is_admin_bar) {
      modalContainer.classList.add('has-admin-bar')
    }
  }

  (window as any).__showroom_modal_container = modalContainer

  // Create inner container for Vue app mounting
  const appContainer = document.createElement('div')
  appContainer.id = 'app-root'
  appContainer.className = base_classes
  shadow.appendChild(appContainer)

  // Load Material Design Icons inside both shadow roots
  const injectMdiLink = (targetShadow: ShadowRoot) => {
    const mdiLink = document.createElement('link')
    mdiLink.rel = 'stylesheet'
    mdiLink.href = 'https://cdn.jsdelivr.net/npm/@mdi/font/css/materialdesignicons.min.css'
    targetShadow.appendChild(mdiLink)
  }
  injectMdiLink(shadow)
  if (modalShadow) injectMdiLink(modalShadow)

  // Load stylesheet
  if (import.meta.env.PROD) {
    const relPath = (window as any).catalog_config?.rel_path || '/wp-content/plugins/1-800-showroom'
    
    // Inject main styles link
    const styleLink = document.createElement('link')
    styleLink.rel = 'stylesheet'
    styleLink.href = `${relPath}/assets/dist/css/ProductsEngine.css?v=${Date.now()}`
    shadow.appendChild(styleLink)

    // Inject styles link inside modal shadow root
    if (modalShadow) {
      const modalStyleLink = styleLink.cloneNode(true) as HTMLLinkElement
      modalShadow.appendChild(modalStyleLink)
    }

    // Copy Vuetify theme variables stylesheet from head into Shadow roots
    const copyThemeStyles = () => {
      const themeStyle = document.getElementById('vuetify-theme-stylesheet')
      if (themeStyle) {
        const existingMain = shadow.querySelector('#vuetify-theme-stylesheet')
        if (!existingMain) {
          const clone = themeStyle.cloneNode(true) as HTMLStyleElement
          shadow.appendChild(clone)
        } else if (existingMain.textContent !== themeStyle.textContent) {
          existingMain.textContent = themeStyle.textContent
        }

        if (modalShadow) {
          const existingModal = modalShadow.querySelector('#vuetify-theme-stylesheet')
          if (!existingModal) {
            const clone = themeStyle.cloneNode(true) as HTMLStyleElement
            modalShadow.appendChild(clone)
          } else if (existingModal.textContent !== themeStyle.textContent) {
            existingModal.textContent = themeStyle.textContent
          }
        }
      }
    }
    const themeObserver = new MutationObserver(() => copyThemeStyles())
    themeObserver.observe(document.head, { childList: true, subtree: true })
    copyThemeStyles()
  } else {
    // Development Mode: Copy all styles from head into both Shadow DOM roots
    const copyStyles = () => {
      const headStyles = document.querySelectorAll('style')
      headStyles.forEach((style, index) => {
        const uniqueId = style.getAttribute('data-vite-dev-id') || style.id || `head-style-${index}`
        
        // Copy to main app shadow
        const existingMain = shadow.querySelector(`style[data-copied-id="${uniqueId}"]`)
        if (!existingMain) {
          const clone = style.cloneNode(true) as HTMLStyleElement
          clone.setAttribute('data-copied-id', uniqueId)
          shadow.appendChild(clone)
        } else if (existingMain.textContent !== style.textContent) {
          existingMain.textContent = style.textContent
        }

        // Copy to modal shadow
        if (modalShadow) {
          const existingModal = modalShadow.querySelector(`style[data-copied-id="${uniqueId}"]`)
          if (!existingModal) {
            const clone = style.cloneNode(true) as HTMLStyleElement
            clone.setAttribute('data-copied-id', uniqueId)
            modalShadow.appendChild(clone)
          } else if (existingModal.textContent !== style.textContent) {
            existingModal.textContent = style.textContent
          }
        }
      })
    }

    // Set up observer to copy styles injected during HMR
    const observer = new MutationObserver(() => copyStyles())
    observer.observe(document.head, { childList: true, subtree: true })
    copyStyles()
  }

  const app = createApp(App)
  const pinia = createPinia()

  pinia.use(piniaPluginPersistedstate)

  const vuetify = CreateVuetifyApp(modalContainer || '#app-root')

  app.use(pinia)
  app.use(vuetify)
  app.mount(appContainer)

  // Reveal root mounting element smoothly after 1 second
  setTimeout(() => {
    rootElement.style.opacity = '1'
  }, 1000)
}
